

import { useParams, useLocation, useNavigate as useRouter } from "react-router-dom";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { setPref } from "@/lib/storage/userPrefs";
import { Plugin } from "@/types/enums/Plugin";
import { useQueryClient } from "@tanstack/react-query";
import { updateEventSidebarOrder } from "@/lib/api/handlers/EventHandling";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";
import type { Capability } from "@/types/Capability";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { smsChannelEnabledOf, smsFixedModeEnabledOf } from "@/lib/sms/smsCapabilities";
import { useEventCompanyRole } from "@/lib/query/hooks/useEventEntitlements";
import { prefetchEventRoute } from "./routePrefetch";
import { normalizePathname } from "@/lib/routing/activePath";
import { eventLayoutEventKey } from "@/lib/query/hooks/useEventLayoutEvent";
import {
  MODULE_ICONS,
  compareSidebarModules,
  isSidebarModuleVisible,
  type SidebarModuleUrl,
} from "./sidebarModules";
import { useGuestTrashVisible } from "@/lib/guests/useGuestTrashVisible";
import { canUseGuestTrash } from "@/lib/guests/guestTrashAccess";

export const useEventSidebar = (
  t: (key: string) => string,
  collapsedProp: boolean,
  pluginsProp: Plugin[],
  eventRole: string,
  sidebarOrderProp?: string[],
) => {
  const capabilities = useCapabilities();
  const { can, isCustom, rowFilters, fieldAllowlist } = capabilities;
  // SMS plugin wave: the SMS page's entry needs the platform switch on too.
  const smsChannelEnabled = smsChannelEnabledOf(capabilities);
  const { eventId } = useParams();
  // W6: the Papierkorb entry shows when the trash is known to be on for this
  // event or holds rows; `/trash/count` 404 (backend without it) ⇒ hidden.
  // The item follows the backend's trash door like the page and the activity
  // row (guestTrashAccess.ts): a row- or field-restricted CUSTOM deleter used
  // to see "Papierkorb" once this browser had learned the flag, and landed on
  // the page's refusal (23.10., audit r3).
  const trashAllowed = canUseGuestTrash({ can, rowFilters, fieldAllowlist });
  const trashVisible = useGuestTrashVisible(eventId, trashAllowed);
  // The event setup journey moved out of the sidebar into the onboarding panel
  // (folded as an event-specific group). No sidebar entry any more.
  const [collapsed, setCollapsed] = useState(collapsedProp);
  const [plugins, setPlugins] = useState<Plugin[]>(pluginsProp);
  const [sidebarOrder, setSidebarOrder] = useState<string[] | undefined>(sidebarOrderProp);
  // SMS plugin wave: "Team numbers" lists the COMPANY's numbers — members of
  // the event's company only, while fixed mode is open. The event's
  // entitlements (the shell reads them already) say who is a member; read
  // only when the SMS group can show at all. A FAILED read keeps the entry:
  // the page says "could not load" with a retry instead of the nav going
  // silent (the page itself sends a non-member to the log).
  const smsNumbersScope =
    smsChannelEnabled && smsFixedModeEnabledOf(capabilities) && plugins.includes(Plugin.SMS) && eventId
      ? eventId
      : null;
  const smsCompanyRole = useEventCompanyRole(smsNumbersScope);
  const smsNumbersVisible =
    smsNumbersScope !== null &&
    (smsCompanyRole.status === "error" || (smsCompanyRole.status === "ready" && smsCompanyRole.member));
  // Canonicalised: a shared link like `.../guests/guestlist/?state=ACCEPTED`
  // arrives with the trailing slash intact, and every active-check below (and
  // in EventSidebar) compares against slash-less module paths.
  const pathname = normalizePathname(useLocation().pathname);
  const router = useRouter();
  const [currentUrl, setCurrentUrl] = useState(pathname);
  const canReorder = eventRole === "OWNER" || eventRole === "ADMIN";

  // Listen for plugin updates
  useEffect(() => {
    const handlePluginsUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ plugins: Plugin[] }>;
      if (customEvent.detail?.plugins) {
        setPlugins(customEvent.detail.plugins);
      }
    };

    window.addEventListener("pluginsUpdated", handlePluginsUpdated);

    return () => {
      window.removeEventListener("pluginsUpdated", handlePluginsUpdated);
    };
  }, []);

  // Listen for sidebar order updates
  useEffect(() => {
    const handleSidebarOrderUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ sidebarOrder: string[] }>;
      if (customEvent.detail?.sidebarOrder) {
        setSidebarOrder(customEvent.detail.sidebarOrder);
      }
    };

    window.addEventListener("sidebarOrderUpdated", handleSidebarOrderUpdated);

    return () => {
      window.removeEventListener("sidebarOrderUpdated", handleSidebarOrderUpdated);
    };
  }, []);

  useEffect(() => {
    setCurrentUrl(pathname);
  }, [pathname]);

  // Sync state from props when they change. Required because the sidebar
  // now mounts while EventLayoutShell's eventQ is still loading (P1.3), so
  // pluginsProp/sidebarOrderProp arrive as their fallback values first and
  // resolve to real data afterwards. Without this sync, useState's
  // initial-value-only semantics leaves the sidebar showing the loading
  // fallback forever (e.g. only Guests visible even when plugins are on).
  // EventLayoutShell pins EMPTY_PLUGINS at module scope so the dep here is
  // stable across the undefined-fallback path and doesn't loop.
  useEffect(() => {
    setPlugins(pluginsProp);
  }, [pluginsProp]);
  useEffect(() => {
    setSidebarOrder(sidebarOrderProp);
  }, [sidebarOrderProp]);

  const toggleSidebar = () => {
    setPref("collapsed", String(!collapsed));
    setCollapsed((prev) => !prev);
  };

  // Each sub-item carries the capability its destination page guards on, so
  // the sidebar can hide entries that would otherwise lead to a locked-door
  // (SNoAccess) — or, for the requests page, a 403 crash. `cap` is either a
  // Capability checked via can(), or a predicate for compound rules (e.g. the
  // Access page is also reachable by CUSTOM users without event-roles:read).
  type SubItem = { name: string; url: string; cap: Capability | (() => boolean); testId?: string };
  type MenuItem = {
    name: string;
    icon?: React.ElementType;
    /** Typed against the shared registry: a renamed url is a compile error. */
    url: SidebarModuleUrl;
    subItems?: SubItem[];
  };

  // Define all menu items. Order + icons + gates come from the shared module
  // registry (sidebarModules.ts) so the onboarding setup track can mirror them.
  const allMenuItems: MenuItem[] = useMemo(() => [
    { name: t("dashboard"), icon: MODULE_ICONS.dashboard, url: "dashboard" },
    { name: t("goals"), icon: MODULE_ICONS.goals, url: "goals" },
    { name: t("app"), icon: MODULE_ICONS.app, url: "app" },
    { name: t("website"), icon: MODULE_ICONS.website, url: "website" },
    {
      name: t("guests"),
      icon: MODULE_ICONS.guests,
      url: "guests",
      subItems: [
        { name: t("guestlist"), url: "guestlist", cap: "guests:read" },
        { name: t("requests"), url: "requests", cap: "guests:read.companions" },
        { name: t("trash"), url: "trash", cap: () => trashAllowed && trashVisible, testId: "guest-trash-sidebar-item" },
      ],
    },
    {
      name: t("checkIn"),
      icon: MODULE_ICONS["check-in"],
      url: "check-in",
      subItems: [
        { name: t("history"), url: "history", cap: "check-in-logs:read" },
        { name: t("devices"), url: "devices", cap: "check-in-devices:read" },
      ],
    },
    // Programme / Speakers / Sponsors: three flat top-level entries, one
    // plugin each (separately extendable) — gated via capabilityMap +
    // pluginMap like every other flat entry.
    // W4/F7: no separate slotting entry any more — slotting lives under
    // Programme (its old routes redirect there).
    { name: t("programme"), icon: MODULE_ICONS.programme, url: "programme" },
    { name: t("speakers"), icon: MODULE_ICONS.speakers, url: "speakers" },
    { name: t("sponsors"), icon: MODULE_ICONS.sponsors, url: "sponsors" },
    { name: t("seating"), icon: MODULE_ICONS.seating, url: "seating" },
    { name: t("attachments"), icon: MODULE_ICONS.attachments, url: "attachments" },
    { name: t("documents"), icon: MODULE_ICONS.documents, url: "documents" },
    {
      name: t("automation"),
      icon: MODULE_ICONS.automation,
      url: "automation",
      subItems: [
        { name: t("automationWorkflows"), url: "workflows", cap: "workflows:read" },
        { name: t("automationLogs"), url: "history", cap: "workflows:read" },
      ],
    },
    {
      name: t("messages"),
      icon: MODULE_ICONS.messaging,
      url: "messaging",
      subItems: [
        { name: t("mail-templates"), url: "mail-templates", cap: "email-templates:read" },
        { name: t("sent-mails"), url: "sent-mails", cap: "sent-emails:read" },
        { name: t("sender"), url: "sender", cap: "event:settings:write" },
      ],
    },
    // SMS plugin wave (F-5): a group like the e-mails — the event's SMS
    // templates first (SMS templates wave, F-4: like "Mail templates"), the
    // company's Team numbers (members of the event's company) and, last like
    // an archive, the sent SMS. The group's own link opens its first visible
    // page, as every group does ("SMS templates", R5); `/sms` and the search
    // row "SMS" open "Sent SMS", the one page every SMS reader has.
    {
      name: t("sms"),
      icon: MODULE_ICONS.sms,
      url: "sms",
      subItems: [
        { name: t("smsTemplates"), url: "templates", cap: "workflows:read", testId: "sms-templates-sidebar-item" },
        { name: t("smsTeamNumbers"), url: "team-numbers", cap: () => smsNumbersVisible && can("workflows:read"), testId: "sms-team-numbers-sidebar-item" },
        { name: t("smsSent"), url: "sent-sms", cap: "workflows:read", testId: "sms-sent-sidebar-item" },
      ],
    },
    //{ name: t("tickets"), icon: IconTicket, url: "ticketing" },
    {
      name: t("settings"),
      icon: MODULE_ICONS.settings,
      url: "settings",
      subItems: [
        { name: t("general"), url: "general", cap: "event:settings:write" },
        { name: t("rulesAndLimits"), url: "rules", cap: "check-in-rules:read" },
        { name: t("plugins"), url: "plugins", cap: "event:settings:write" },
        // W4-3 / 4e: staff contacts who get an arrival notice. The page gates
        // OWNER/ADMIN itself (assertRole), like general and plugins.
        { name: t("notifications"), url: "notifications", cap: "event:settings:write" },
        // Event media library (images/videos used by the builders).
        { name: t("media"), url: "media", cap: "event:settings:read" },
        // Access page is also reachable by CUSTOM users without event-roles:read
        // (their "My access" view) — mirror EventAccessTabs' guard.
        { name: t("accessNav"), url: "access", cap: () => can("event-roles:read") || isCustom },
      ],
    },
  ], [t, can, isCustom, trashAllowed, trashVisible, smsNumbersVisible]);

  // Filter menu items based on enabled plugins, capabilities, and role, then sort by saved order
  const menuItems = useMemo(() => {
    // P4: each top-level sidebar entry is gated by a read capability AND (for
    // most) a plugin — both live in the shared module registry now, so the
    // onboarding setup track applies exactly the same rules. Sub-item gating
    // goes through per-page entry guards (Phase E) — keeping MenuItem.subItems
    // shape unchanged avoids rippling through every consumer of the hook.
    const filtered = allMenuItems.filter((item) =>
      isSidebarModuleVisible(item.url, { plugins, can, smsChannelEnabled }),
    );

    // Sub-item gating: hide entries whose destination page would deny access.
    // A predicate `cap` (Access) is evaluated; everything else goes through
    // can(). Parents whose sub-items all drop out are removed too — otherwise
    // EventSidebar's `subItems[0].url` (primaryUrl) would read undefined.
    const subVisible = (s: SubItem) => (typeof s.cap === "function" ? s.cap() : can(s.cap));
    const withSubs = filtered
      .map((item) => (item.subItems ? { ...item, subItems: item.subItems.filter(subVisible) } : item))
      .filter((item) => !item.subItems || item.subItems.length > 0);

    if (!sidebarOrder || sidebarOrder.length === 0) return withSubs;

    // Saved order first, canonical order as the tie-break (items missing from
    // the saved order go to the end, keeping their canonical sequence).
    return [...withSubs].sort((a, b) => compareSidebarModules(a.url, b.url, sidebarOrder));
  }, [allMenuItems, plugins, eventRole, sidebarOrder, can, isCustom, smsChannelEnabled]);

  const navigateTo = useCallback(
    (url: string) => {
      // DOM dispatchEvent is synchronous: any `navigationCancelled` dispatched
      // from a `navigationStart` listener (see useUnsavedChanges) runs to
      // completion before dispatchEvent() returns, so we can check the flag
      // immediately. No setTimeout needed.
      let navigationCancelled = false;
      const cancelHandler = () => {
        navigationCancelled = true;
      };

      window.addEventListener("navigationCancelled", cancelHandler, {
        once: true,
      });
      window.dispatchEvent(
        new CustomEvent("navigationStart", { detail: { url } }),
      );
      window.removeEventListener("navigationCancelled", cancelHandler);

      if (!navigationCancelled) {
        setCurrentUrl(normalizePathname(url));
        router(url);
      }
    },
    [router],
  );

  const prefetch = useCallback((url: string) => {
    prefetchEventRoute(url);
  }, []);

  const queryClient = useQueryClient();
  const reorderItems = useCallback(
    async (newOrder: string[]) => {
      const previousOrder = sidebarOrder;
      setSidebarOrder(newOrder);

      try {
        const response = await updateEventSidebarOrder(eventId as string, newOrder);
        if (response && "isServerError" in response) {
          throwServerError(response);
        }
        // The shell's event query (60s staleTime) carries sidebarOrder and the
        // prop-sync effect above re-seeds from it — patch the cache so
        // re-entering the event within staleTime doesn't revert the drag.
        queryClient.setQueryData(
          eventLayoutEventKey(eventId),
          (prev: { sidebarOrder?: string[] } | undefined) =>
            prev ? { ...prev, sidebarOrder: newOrder } : prev,
        );
        window.dispatchEvent(
          new CustomEvent("sidebarOrderUpdated", {
            detail: { sidebarOrder: newOrder },
          }),
        );
      } catch {
        setSidebarOrder(previousOrder);
      }
    },
    [eventId, sidebarOrder, queryClient],
  );

  return {
    eventId,
    collapsed,
    toggleSidebar,
    menuItems,
    currentUrl,
    navigateTo,
    prefetch,
    canReorder,
    reorderItems,
  };
};
